/**
 * Stamp a PNG with the real-world size it is meant to be printed or opened at.
 *
 * The studio never resamples: a photo goes out with every pixel it arrived with. But pixels
 * alone say nothing about SIZE — a 4000px-wide picture with no physical size recorded is opened
 * by every viewer at its own guess (usually 96 dpi, so about a metre wide), which is why a
 * 5cm object saved from the sheet did not measure 5cm when you opened the file again.
 *
 * PNG carries that information in a `pHYs` chunk: pixels per metre on each axis, which is
 * exactly `pixels / (cm / 100)`. Writing it changes no image data at all — the file still has
 * the same pixels — it only tells whoever opens it how big those pixels are meant to be. Image
 * viewers, word processors and print dialogs all honour it.
 *
 * Everything here works on the raw bytes so it holds for any PNG, including one that already
 * carries a pHYs chunk from somewhere else (that one is replaced rather than duplicated).
 */

const PNG_SIG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

// CRC-32 as PNG defines it, over the chunk's type and data.
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

const crc32 = (bytes) => {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
};

const u32 = (view, offset) => view.getUint32(offset, false);

export const STUDIO_TAG = 'CreativeStudio';

/**
 * ── Why the size is written twice ───────────────────────────────────────────────────────────
 *
 * A density field cannot hold a centimetre exactly. PNG counts whole pixels per METRE, which is
 * fine — 18.2cm came back as 18.2011. JPEG counts whole dots per INCH, and at ordinary photo
 * sizes one dpi step is about a millimetre: 18.2cm went out and 18.25cm came back. Save, open,
 * save again and it walks. For a studio whose whole point is that a 5cm object is 5cm, "close"
 * is the wrong answer, and a photograph saved on its own is usually a JPEG, so this was the
 * commonest case rather than an exotic one.
 *
 * So the density stays — it is what every other program reads, and it should be as right as its
 * format allows — and the EXACT measurement goes in the text record beside it, which is ours and
 * has no such limit. Reading our own file, the text wins; reading anyone else's, the density is
 * all there is and nothing has changed.
 */
const sizeText = (cmW, cmH) =>
  `${STUDIO_TAG} ${(+cmW).toFixed(4).replace(/\.?0+$/, '')}x${(+cmH).toFixed(4).replace(/\.?0+$/, '')}cm`;
/** The exact size out of one of our text records, or null if it is an older one without it. */
export const parseSizeText = (txt) => {
  const m = /CreativeStudio\s+([0-9]*\.?[0-9]+)x([0-9]*\.?[0-9]+)cm/.exec(String(txt || ''));
  if (!m) return null;
  const cmW = parseFloat(m[1]), cmH = parseFloat(m[2]);
  return cmW > 0 && cmH > 0 ? { cmW, cmH } : null;
};

/** A tEXt chunk carrying `Software=CreativeStudio 18.2x10cm` — our mark, and the exact size. */
const textChunk = (cmW, cmH) => {
  const kw = 'Software';
  const val = sizeText(cmW, cmH);
  const body = new Uint8Array(4 + kw.length + 1 + val.length);
  body.set([0x74, 0x45, 0x58, 0x74], 0);                       // "tEXt"
  for (let i = 0; i < kw.length; i++) body[4 + i] = kw.charCodeAt(i);
  body[4 + kw.length] = 0;
  for (let i = 0; i < val.length; i++) body[5 + kw.length + i] = val.charCodeAt(i);
  const out = new Uint8Array(body.length + 8);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, body.length - 4, false);
  out.set(body, 4);
  dv.setUint32(out.length - 4, crc32(body), false);
  return out;
};

/** A complete pHYs chunk: length, type, both axes in pixels per metre, unit 1 (the metre). */
const physChunk = (ppmX, ppmY) => {
  const body = new Uint8Array(13);          // 4 type + 9 data
  const dv = new DataView(body.buffer);
  body.set([0x70, 0x48, 0x59, 0x73], 0);    // "pHYs"
  dv.setUint32(4, ppmX, false);
  dv.setUint32(8, ppmY, false);
  body[12] = 1;                             // unit specifier: metre
  const out = new Uint8Array(21);           // 4 length + 13 + 4 crc
  const odv = new DataView(out.buffer);
  odv.setUint32(0, 9, false);               // data length, type and crc excluded
  out.set(body, 4);
  odv.setUint32(17, crc32(body), false);
  return out;
};

/**
 * Return a copy of `blob` declaring itself `cmWidth` x `cmHeight` centimetres.
 *
 * Returns the blob untouched — never throws — when it is not a PNG, when the sizes are not
 * usable, or when the bytes cannot be parsed: a saved file that is merely missing its physical
 * size is far better than a save that failed.
 */
export async function withPhysicalSize(blob, cmWidth, cmHeight) {
  try {
    if (!blob) return blob;
    if (!(cmWidth > 0) || !(cmHeight > 0)) return blob;
    // A selected, untouched photo goes out as its own original bytes, and those are usually a
    // JPEG — which keeps its size in a different place. Without this branch the commonest case
    // of all (save one photo) kept whatever size its camera or editor had recorded.
    if (blob.type === 'image/jpeg') return await withPhysicalSizeJpeg(blob, cmWidth, cmHeight);
    if (blob.type !== 'image/png') return blob;

    const buf = new Uint8Array(await blob.arrayBuffer());
    if (buf.length < 8 || PNG_SIG.some((b, i) => buf[i] !== b)) return blob;

    const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    // IHDR always comes first and carries the pixel dimensions.
    const pxW = u32(view, 16), pxH = u32(view, 20);
    if (!pxW || !pxH) return blob;

    const ppmX = Math.round(pxW / (cmWidth / 100));
    const ppmY = Math.round(pxH / (cmHeight / 100));
    if (!(ppmX > 0) || !(ppmY > 0)) return blob;

    // Walk the chunks: drop any existing pHYs, and note where the image data starts — the new
    // chunk has to sit before the first IDAT to be legal.
    const keep = [];
    let pos = 8, idatAt = -1;
    while (pos + 8 <= buf.length) {
      const len = u32(view, pos);
      const type = String.fromCharCode(buf[pos + 4], buf[pos + 5], buf[pos + 6], buf[pos + 7]);
      const end = pos + 12 + len;
      if (end > buf.length) return blob;                  // truncated: leave it alone
      if (type === 'IDAT' && idatAt < 0) idatAt = keep.length;
      const isOurText = type === 'tEXt' &&
        String.fromCharCode(...buf.subarray(pos + 8, Math.min(pos + 8 + 8, buf.length))) === 'Software';
      if (type !== 'pHYs' && !isOurText) keep.push(buf.subarray(pos, end));
      pos = end;
      if (type === 'IEND') break;
    }
    if (idatAt < 0) return blob;

    const parts = [PNG_SIG, ...keep.slice(0, idatAt), physChunk(ppmX, ppmY), textChunk(cmWidth, cmHeight), ...keep.slice(idatAt)];
    return new Blob(parts.map(p => (Array.isArray(p) ? new Uint8Array(p) : p)), { type: 'image/png' });
  } catch {
    return blob;
  }
}

/** The dpi a size stamp describes, handy for logging and tests. */
export const cmToDpi = (px, cm) => (cm > 0 ? (px / (cm / 2.54)) : 0);

/**
 * The same job for a JPEG, which keeps its physical size somewhere else entirely.
 *
 * JPEG has no pHYs. Density lives in the JFIF APP0 segment right after the start marker:
 * a units byte (1 = dots per inch) and two 16-bit densities. Most photos arrive with units 0
 * ("no units, aspect ratio only"), which is why a photo saved straight through opened at
 * whatever size the viewer felt like. This patches that segment in place, or inserts one when
 * the file has none — the compressed image data is never touched, so no generation of quality
 * is lost the way re-encoding would lose it.
 */
async function withPhysicalSizeJpeg(blob, cmWidth, cmHeight) {
  const buf = new Uint8Array(await blob.arrayBuffer());
  if (buf.length < 4 || buf[0] !== 0xff || buf[1] !== 0xd8) return blob;   // not a JPEG

  // Pixel dimensions come from the frame header (any SOFn but the arithmetic/lossless ones).
  let pos = 2, pxW = 0, pxH = 0;
  while (pos + 4 <= buf.length) {
    if (buf[pos] !== 0xff) { pos++; continue; }
    const marker = buf[pos + 1];
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) { pos += 2; continue; }
    const len = (buf[pos + 2] << 8) | buf[pos + 3];
    const isSof = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isSof && pos + 9 < buf.length) {
      pxH = (buf[pos + 5] << 8) | buf[pos + 6];
      pxW = (buf[pos + 7] << 8) | buf[pos + 8];
      break;
    }
    if (marker === 0xda) break;                 // image data: no frame header found
    pos += 2 + len;
  }
  if (!pxW || !pxH) return blob;

  const dpiX = Math.round(pxW / (cmWidth / 2.54));
  const dpiY = Math.round(pxH / (cmHeight / 2.54));
  if (!(dpiX > 0) || !(dpiY > 0) || dpiX > 65535 || dpiY > 65535) return blob;

  const isJfifApp0 = (at) =>
    buf[at] === 0xff && buf[at + 1] === 0xe0 &&
    buf[at + 4] === 0x4a && buf[at + 5] === 0x46 && buf[at + 6] === 0x49 && buf[at + 7] === 0x46 && buf[at + 8] === 0x00;

  // Walk the segments once: find the first JFIF APP0 (wherever it sits — after a second save
  // it is no longer at offset 2, because our own comment now precedes it) and note whether our
  // mark is already there. Patching in place is what keeps a re-save from stacking up a second
  // density block that decoders and our own reader would then disagree about.
  // Every one of OUR comments is noted so it can be dropped: the new one carries the new size,
  // and leaving the old one in place would mean a re-saved file stating two different sizes,
  // with the reader taking whichever it met first — the stale one.
  let app0At = -1;
  const ourComments = [];
  {
    let q = 2;
    while (q + 4 <= buf.length) {
      if (buf[q] !== 0xff) { q++; continue; }
      const mk = buf[q + 1];
      if (mk === 0xd8 || mk === 0x01 || (mk >= 0xd0 && mk <= 0xd7)) { q += 2; continue; }
      if (mk === 0xda) break;
      const ln = (buf[q + 2] << 8) | buf[q + 3];
      if (mk === 0xe0 && app0At < 0 && isJfifApp0(q)) app0At = q;
      if (mk === 0xfe && String.fromCharCode(...buf.subarray(q + 4, q + 2 + ln)).includes(STUDIO_TAG)) {
        ourComments.push([q, q + 2 + ln]);
      }
      q += 2 + ln;
    }
  }

  const comment = (() => {
    const txt = sizeText(cmWidth, cmHeight);
    const body = new Uint8Array(4 + txt.length);
    body[0] = 0xff; body[1] = 0xfe;
    const len = 2 + txt.length;
    body[2] = len >> 8; body[3] = len & 0xff;
    for (let i = 0; i < txt.length; i++) body[4 + i] = txt.charCodeAt(i);
    return body;
  })();

  /**
   * The file rebuilt: our old comments cut out, the new pieces put in at one place.
   *
   * Where that place is matters. JFIF asks for its APP0 to be the FIRST segment after the start
   * marker, and a comment slipped in ahead of it makes the file no longer a JFIF file to a
   * strict reader — so the density we just took care to write would be the one thing nobody
   * reads. The comment therefore goes after the JFIF header, never before it.
   */
  const rebuild = (src, insertAt, pieces) => {
    const events = ourComments.map(([s, e]) => ({ at: s, skipTo: e }));
    events.push({ at: insertAt, put: pieces });
    events.sort((a, b) => a.at - b.at || (a.put ? 1 : -1));
    const out = [];
    let pos = 0;
    for (const ev of events) {
      if (ev.at > pos) out.push(src.subarray(pos, ev.at));
      if (ev.put) out.push(...ev.put);
      pos = Math.max(pos, ev.skipTo ?? ev.at);
    }
    if (pos < src.length) out.push(src.subarray(pos));
    return out;
  };

  if (app0At >= 0) {
    const out = buf.slice();
    out[app0At + 11] = 1;                       // units: dots per inch
    out[app0At + 12] = dpiX >> 8; out[app0At + 13] = dpiX & 0xff;
    out[app0At + 14] = dpiY >> 8; out[app0At + 15] = dpiY & 0xff;
    const app0End = app0At + 2 + ((buf[app0At + 2] << 8) | buf[app0At + 3]);
    return new Blob(rebuild(out, app0End, [comment]), { type: 'image/jpeg' });
  }

  // No JFIF header at all: insert a minimal one immediately after the start marker.
  const app0 = new Uint8Array([
    0xff, 0xe0, 0x00, 0x10, 0x4a, 0x46, 0x49, 0x46, 0x00, 0x01, 0x02, 0x01,
    dpiX >> 8, dpiX & 0xff, dpiY >> 8, dpiY & 0xff, 0x00, 0x00,
  ]);
  return new Blob(rebuild(buf, 2, [app0, comment]), { type: 'image/jpeg' });
}


/**
 * Read back the real-world size a file declares — but only from a file this studio wrote.
 *
 * Why the restriction: cameras and editors routinely stamp 72 dpi into a photo, which is not a
 * statement about how big the picture is meant to be, it is a default nobody looked at. Honour
 * that on import and a 4000px holiday snap arrives claiming to be a metre and a half wide. Our
 * own exports carry a marker alongside the size, and only those are taken at their word; every
 * other picture keeps the old behaviour of arriving at a sensible fraction of the sheet.
 *
 * @returns {Promise<{cmW:number, cmH:number}|null>}
 */
export async function readPhysicalSize(blob) {
  try {
    if (!blob) return null;
    const buf = new Uint8Array(await blob.arrayBuffer());

    // ── PNG ──
    if (buf.length > 8 && !PNG_SIG.some((b, i) => buf[i] !== b)) {
      const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
      const pxW = u32(view, 16), pxH = u32(view, 20);
      let ppmX = 0, ppmY = 0, unit = 0, tagged = false, exact = null;
      let pos = 8;
      while (pos + 8 <= buf.length) {
        const len = u32(view, pos);
        const type = String.fromCharCode(buf[pos + 4], buf[pos + 5], buf[pos + 6], buf[pos + 7]);
        if (type === 'pHYs' && len >= 9) {
          ppmX = u32(view, pos + 8); ppmY = u32(view, pos + 12); unit = buf[pos + 16];
        } else if (type === 'tEXt') {
          const txt = String.fromCharCode(...buf.subarray(pos + 8, pos + 8 + len));
          if (txt.includes(STUDIO_TAG)) { tagged = true; exact = exact || parseSizeText(txt); }
        }
        pos += 12 + len;
        if (type === 'IEND' || pos > buf.length) break;
      }
      if (!tagged) return null;
      // The measurement we wrote down, when there is one. Density is a rounded restatement of
      // it for everyone else's benefit, so it is only consulted for our older files.
      if (exact) return exact;
      if (unit !== 1 || !ppmX || !ppmY || !pxW || !pxH) return null;
      return { cmW: (pxW / ppmX) * 100, cmH: (pxH / ppmY) * 100 };
    }

    // ── JPEG ──
    if (buf.length > 4 && buf[0] === 0xff && buf[1] === 0xd8) {
      let pos = 2, pxW = 0, pxH = 0, dpiX = 0, dpiY = 0, units = 0, tagged = false, exact = null;
      while (pos + 4 <= buf.length) {
        if (buf[pos] !== 0xff) { pos++; continue; }
        const marker = buf[pos + 1];
        if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) { pos += 2; continue; }
        const len = (buf[pos + 2] << 8) | buf[pos + 3];
        // First JFIF block wins — that is the one a decoder reads, so it must be the one we
        // read too, or a re-saved file could report a size no viewer would ever show.
        if (marker === 0xe0 && !dpiX && String.fromCharCode(buf[pos + 4], buf[pos + 5], buf[pos + 6], buf[pos + 7]) === 'JFIF') {
          units = buf[pos + 11];
          dpiX = (buf[pos + 12] << 8) | buf[pos + 13];
          dpiY = (buf[pos + 14] << 8) | buf[pos + 15];
        } else if (marker === 0xfe) {
          const txt = String.fromCharCode(...buf.subarray(pos + 4, pos + 2 + len));
          if (txt.includes(STUDIO_TAG)) { tagged = true; exact = exact || parseSizeText(txt); }
        }
        const isSof = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
        if (isSof && pos + 9 < buf.length) {
          pxH = (buf[pos + 5] << 8) | buf[pos + 6];
          pxW = (buf[pos + 7] << 8) | buf[pos + 8];
        }
        if (marker === 0xda) break;
        pos += 2 + len;
      }
      if (!tagged) return null;
      // As above: dots per INCH is far coarser than a centimetre — one step is about a
      // millimetre at photo sizes — so our own measurement is the one to believe.
      if (exact) return exact;
      if (!pxW || !pxH || !dpiX || !dpiY) return null;
      if (units === 1) return { cmW: (pxW / dpiX) * 2.54, cmH: (pxH / dpiY) * 2.54 };
      if (units === 2) return { cmW: pxW / dpiX, cmH: pxH / dpiY };
      return null;
    }
    return null;
  } catch {
    return null;
  }
}
